import { apiClient } from '@/lib/api/api-client';
import axiosInstance from '@/lib/api/axios-instance';
import { ENDPOINTS } from '@/lib/api/endpoints';
import {
  Customer,
  CustomerContact,
  CreateCustomerData,
  UpdateCustomerData,
  CreateContactData,
  UpdateContactData,
  CustomerSegment,
  UnifiedListParams,
  UnifiedListResponse,
} from '../types/customer.types';
import { ImportResult } from '@/features/master-data/types/master-data.types';

/**
 * Servicio para gestión de clientes y contactos
 */
class CustomerService {
  async getCustomers(search?: string, segment?: CustomerSegment): Promise<Customer[]> {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (segment) params.set('segment', segment);
    // Este endpoint alimenta dropdowns con búsqueda client-side: pedimos todos
    // los clientes del tenant en una sola pasada en vez de paginar.
    params.set('limit', '5000');
    const url = `${ENDPOINTS.CUSTOMERS}?${params.toString()}`;
    return apiClient.get<Customer[]>(url);
  }

  async getCustomerById(id: string): Promise<Customer> {
    return apiClient.get<Customer>(ENDPOINTS.CUSTOMER_BY_ID(id));
  }

  async createCustomer(data: CreateCustomerData): Promise<Customer> {
    return apiClient.post<Customer>(ENDPOINTS.CUSTOMERS, data);
  }

  async updateCustomer(id: string, data: UpdateCustomerData): Promise<Customer> {
    return apiClient.put<Customer>(ENDPOINTS.CUSTOMER_BY_ID(id), data);
  }

  async deleteCustomer(id: string): Promise<void> {
    return apiClient.delete<void>(ENDPOINTS.CUSTOMER_BY_ID(id));
  }

  async addContact(customerId: string, data: CreateContactData): Promise<CustomerContact> {
    return apiClient.post<CustomerContact>(ENDPOINTS.CUSTOMER_CONTACTS(customerId), data);
  }

  async updateContact(
    customerId: string,
    contactId: string,
    data: UpdateContactData
  ): Promise<CustomerContact> {
    return apiClient.put<CustomerContact>(
      ENDPOINTS.CUSTOMER_CONTACT_BY_ID(customerId, contactId),
      data
    );
  }

  async deleteContact(customerId: string, contactId: string): Promise<void> {
    return apiClient.delete<void>(ENDPOINTS.CUSTOMER_CONTACT_BY_ID(customerId, contactId));
  }

  async importCustomers(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImportResult>(ENDPOINTS.CUSTOMERS_IMPORT, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async listUnified(params: UnifiedListParams = {}): Promise<UnifiedListResponse> {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.source) qs.set('source', params.source);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    // El endpoint /unified devuelve { success, data, pagination } y necesitamos
    // tanto data como pagination. apiClient.get unwrappea a response.data.data,
    // así que aquí usamos axiosInstance directo para preservar la envoltura.
    const response = await axiosInstance.get<UnifiedListResponse>(
      `${ENDPOINTS.CUSTOMERS_UNIFIED}${suffix}`
    );
    return response.data;
  }
}

export const customerService = new CustomerService();
export default customerService;
